/**
 * Yard Queue Engine (Deterministic)
 *
 * Derives the yard queue from existing appointment + dock data.
 * No API calls, no side effects.
 *
 * Queue = trucks that are checked in but NOT actively occupying a dock.
 * Ready = a queued truck with at least one eligible dock recommendation.
 */
import type { Appointment } from '../types/appointments';
import { safeDateMs } from './cascadePrediction';
import { getRankedDockAssignments } from './predictiveDockAssignment';
import { extractPOMetadataForScoring } from './poMetadataHelpers';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface YardQueueItem {
    appointment: Appointment;
    queueState: 'waiting' | 'ready';
    minutesWaiting: number;
    recommendedDockId?: string;
    recommendedDockNumber?: string;
    reasons: string[];
}

export interface YardQueueResult {
    items: YardQueueItem[];
    totalWaiting: number;
    readyCount: number;
}

interface QueueDock {
    id: string;
    dockNumber: string;
    type: string;
    status?: string | null;
    // INV-4.1: Capability tags for compatibility scoring
    temperatureZone?: string | null;
    handlingCapabilities?: string | null;
}

// ─── Engine ───────────────────────────────────────────────────────────────────

/**
 * Compute yard queue from today's schedule.
 *
 * A truck is "queued" if:
 *   1. checkInTime exists
 *   2. No checkOutTime
 *   3. Not already an active dock occupant (i.e. not occupying a dock right now)
 *
 * A queued truck is "ready" if there is at least one eligible dock with a recommendation.
 */
export function computeYardQueue(
    todaySchedule: Appointment[],
    activeOccupants: Appointment[],
    docks: QueueDock[],
    now: number,
    warehouseTimeZone?: string
): YardQueueResult {
    // Build set of appointment IDs that are active occupants (on a dock)
    // Defensive: normalize dock IDs from all possible field names
    const occupantIds = new Set<string>();
    const occupantDockIds = new Set<string>();
    for (const occ of activeOccupants) {
        const dockId = occ.dockId || occ.dock_id || (occ as any).dock?.id;
        if (dockId) {
            occupantIds.add(occ.id);
            occupantDockIds.add(dockId);
        }
    }

    // Find queued trucks — ONLY checked_in (arrived in yard, waiting for dock)
    const queued: Appointment[] = [];
    for (const appt of todaySchedule) {
        const status = (appt.status || '').toLowerCase();
        // Must be checked_in (arrived onsite)
        if (status !== 'checked_in') continue;
        // Must be checked in (timestamp confirmation)
        if (!appt.checkInTime) continue;
        // Must not be checked out
        if (appt.checkOutTime) continue;
        // Must not be an active dock occupant (by appointment ID or by dock ID)
        if (occupantIds.has(appt.id)) continue;
        const apptDockId = appt.dockId || appt.dock_id || (appt as any).dock?.id;
        if (apptDockId && occupantDockIds.has(apptDockId)) continue;

        queued.push(appt);
    }

    // Score each queued truck
    const items: YardQueueItem[] = queued.map((appt) => {
        const checkInMs = safeDateMs(appt.checkInTime);
        const minutesWaiting = !isNaN(checkInMs) ? Math.max(0, Math.round((now - checkInMs) / 60000)) : 0;

        // Get dock recommendation using existing predictive engine
        // INV-4.1: Pass PO compatibility metadata for temperature/handling scoring
        const poMeta = extractPOMetadataForScoring(appt.linkedPOs);
        const ranked = getRankedDockAssignments(appt, docks, todaySchedule, activeOccupants, now, warehouseTimeZone, poMeta);
        const topCandidate = ranked.find(c => c.isEligible && c.score > 0);

        const isReady = !!topCandidate;
        const reasons: string[] = [];

        // Only surface dock recommendation for READY items
        if (isReady && topCandidate) {
            for (const r of topCandidate.reasons.slice(0, 2)) {
                reasons.push(r.label);
            }
        }

        return {
            appointment: appt,
            queueState: isReady ? 'ready' : 'waiting',
            minutesWaiting,
            recommendedDockId: isReady ? topCandidate?.dockId : undefined,
            recommendedDockNumber: isReady ? topCandidate?.dockNumber : undefined,
            reasons,
        };
    });

    // Sort: ready first, then by longest waiting
    items.sort((a, b) => {
        if (a.queueState !== b.queueState) return a.queueState === 'ready' ? -1 : 1;
        return b.minutesWaiting - a.minutesWaiting;
    });

    return {
        items,
        totalWaiting: items.length,
        readyCount: items.filter(i => i.queueState === 'ready').length,
    };
}
